import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "@/lib/google-maps";
import { cn } from "@/lib/utils";

export function MapPreview({
  lat,
  lng,
  placeId,
  address,
  className,
  zoom = 15,
}: {
  lat?: number | null;
  lng?: number | null;
  placeId?: string | null;
  address?: string | null;
  className?: string;
  zoom?: number;
}) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [center, setCenter] = useState<{ lat: number; lng: number } | null>(() => {
    const a = lat != null ? Number(lat) : null;
    const b = lng != null ? Number(lng) : null;
    const ok = a != null && b != null && !Number.isNaN(a) && !Number.isNaN(b);
    return ok ? { lat: a, lng: b } : null;
  });

  useEffect(() => {
    let mounted = true;
    loadGoogleMaps({ libraries: ["marker"] })
      .then(() => {
        if (!mounted) return;
        setReady(true);
      })
      .catch(() => {
        if (!mounted) return;
        setFailed(true);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const a = lat != null ? Number(lat) : null;
    const b = lng != null ? Number(lng) : null;
    const ok = a != null && b != null && !Number.isNaN(a) && !Number.isNaN(b);
    if (!ok) return;
    setCenter({ lat: a, lng: b });
  }, [lat, lng]);

  // If coordinates are missing, attempt to resolve via placeId/address (best-effort)
  useEffect(() => {
    if (!ready || failed) return;
    if (center) return;

    const pid = String(placeId || "").trim();
    const addr = String(address || "").trim();
    if (!pid && !addr) return;

    const google = (window as any).google;
    if (!google?.maps || typeof google.maps.Geocoder !== "function") return;

    let cancelled = false;
    setResolving(true);
    const geocoder = new google.maps.Geocoder();

    const req: any = pid ? { placeId: pid } : { address: addr };
    geocoder.geocode(req, (results: any[], status: string) => {
      if (cancelled) return;
      setResolving(false);
      if (status !== "OK" || !Array.isArray(results) || results.length === 0) return;
      const loc = results?.[0]?.geometry?.location;
      const nextLat = typeof loc?.lat === "function" ? Number(loc.lat()) : null;
      const nextLng = typeof loc?.lng === "function" ? Number(loc.lng()) : null;
      if (nextLat == null || nextLng == null) return;
      if (Number.isNaN(nextLat) || Number.isNaN(nextLng)) return;
      setCenter({ lat: nextLat, lng: nextLng });
    });

    return () => {
      cancelled = true;
    };
  }, [ready, failed, center, placeId, address]);

  useEffect(() => {
    if (!ready || failed) return;
    if (!mapRef.current) return;
    const google = (window as any).google;
    if (!google?.maps || typeof google.maps.Map !== "function") {
      setFailed(true);
      return;
    }

    if (!center) return;

    try {
      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new google.maps.Map(mapRef.current, {
          center,
          zoom,
          disableDefaultUI: true,
          clickableIcons: false,
          gestureHandling: "cooperative",
        });
        markerRef.current = new google.maps.Marker({
          position: center,
          map: mapInstanceRef.current,
        });
      } else {
        mapInstanceRef.current.setCenter(center);
        markerRef.current?.setPosition(center);
      }
    } catch {
      setFailed(true);
    }
  }, [ready, failed, center, zoom]);

  if (!ready || failed) {
    return null;
  }

  if (!center) {
    return (
      <div
        className={cn(
          "flex items-center justify-center text-xs text-muted-foreground bg-muted/20",
          className,
        )}
      >
        {resolving ? "Loading map…" : "Map preview unavailable"}
      </div>
    );
  }

  return <div ref={mapRef} className={className} />;
}


